import { PlatformCartridgeWidget, PlatformWidget } from '../../models/dashboard.models';
import {
  AdminWidgetComponent,
  ContactRequestsWidgetComponent,
  FurtherQualificationProgressWidgetComponent,
  FurtherQualificationStartWidgetComponent,
  GuidedTourAdminAreaComponent,
  OrderRequestsWidgetComponent,
  PlatformRequestsWidgetComponent,
  PriceRequestsWidgetComponent,
} from '../widget/platform-widget';
import { StaticRole } from '@markant-platform/shared/user/model';
import { UsageReportWidgetComponent } from '../widget/platform-cartridge-widget/usage-report-widget/usage-report-widget.component';
import { AppConfigService, AppFeature } from '@markant-platform/shared/data-access';
import { BlockedServiceWidgetComponent } from '../widget/platform-widget/blocked-service-widget/blocked-service-widget.component';
import { ReassignServiceRolesWidgetComponent } from '../widget/platform-widget/reassign-service-roles-widget/reassign-service-roles-widget.component';
import { ServiceCatalogWidgetComponent } from '../widget/platform-widget/service-catalog-widget/service-catalog-widget.component';
import { OrganizationDetailData, QualificationLevel } from 'data-model';

export class DashboardConfig {
  static readonly guidedTourEnabled: boolean = AppConfigService.isFeatureEnabled(AppFeature.GUIDED_TOUR);

  private static readonly checkFurtherQualificationStart = (organization: OrganizationDetailData): boolean => {
    return !organization.openFurtherQualificationProcess;
  };
  private static readonly checkFurtherQualificationProgress = (organization: OrganizationDetailData): boolean => {
    return !!organization.openFurtherQualificationProcess;
  };

  static readonly PLATFORM_WIDGETS: {
    STATIC: PlatformWidget[];
    PLATFORM: PlatformWidget[];
    PLATFORM_CARTRIDGE: PlatformCartridgeWidget[];
  } = {
    STATIC: [
      {
        widgetType: 'PLATFORM',
        id: 'further-qualification-start-widget',
        size: '1x1',
        displayName: 'platformWidget.furtherQualification.start.displayName',
        component: FurtherQualificationStartWidgetComponent,
        allowedRoles: [
          StaticRole.ORGANIZATION_ADMIN,
          StaticRole.ORGANIZATION_MANAGER,
          StaticRole.ORGANIZATION_MEMBER,
          StaticRole.DEMO_USER,
        ],
        showInitial: true,
        isNotRemovable: true,
        allowedQualificationLevels: [QualificationLevel.LEVEL_2, QualificationLevel.LEVEL_1],
        validationFunction: DashboardConfig.checkFurtherQualificationStart,
      },
      {
        widgetType: 'PLATFORM',
        id: 'further-qualification-progress-widget',
        size: '1x2',
        displayName: 'platformWidget.furtherQualification.progress.displayName',
        component: FurtherQualificationProgressWidgetComponent,
        allowedRoles: [
          StaticRole.ORGANIZATION_ADMIN,
          StaticRole.ORGANIZATION_MANAGER,
          StaticRole.ORGANIZATION_MEMBER,
          StaticRole.DEMO_USER,
        ],
        showInitial: true,
        isNotRemovable: true,
        allowedQualificationLevels: [QualificationLevel.LEVEL_2, QualificationLevel.LEVEL_1],
        validationFunction: DashboardConfig.checkFurtherQualificationProgress,
      },
    ],
    PLATFORM: [
      {
        widgetType: 'PLATFORM',
        id: 'guided-tour-oa-admin-area',
        size: '1x1',
        displayName: 'platformWidget.guidedTour.guided-tour-oa-admin-area.displayName',
        component: GuidedTourAdminAreaComponent,
        allowedRoles: [StaticRole.ORGANIZATION_ADMIN],
        showInitial: true,
        disabled: !DashboardConfig.guidedTourEnabled,
      },
      {
        widgetType: 'PLATFORM',
        id: 'admin-widget',
        size: '1x1',
        displayName: 'platformWidget.admin.displayName',
        component: AdminWidgetComponent,
        allowedRoles: [StaticRole.PLATFORM_ADMIN, StaticRole.ORGANIZATION_ADMIN, StaticRole.LEISTUNG_ADMIN],
        showInitial: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'service-catalog-widget',
        size: '1x1',
        displayName: 'platformWidget.serviceCatalog.displayName',
        component: ServiceCatalogWidgetComponent,
        allowedRoles: [StaticRole.ORGANIZATION_ADMIN, StaticRole.ORGANIZATION_MEMBER, StaticRole.DEMO_USER],
        allowedQualificationLevels: [
          QualificationLevel.LEVEL_3,
          QualificationLevel.LEVEL_2,
          QualificationLevel.LEVEL_1,
        ],
        showInitial: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'admin-platform-requests-widget',
        size: '1x1',
        displayName: 'platformWidget.admin.platformRequests.widgetName',
        component: PlatformRequestsWidgetComponent,
        allowedRoles: [StaticRole.PLATFORM_ADMIN],
        showInitial: true,
        routerLink: '/backoffice/platform-requests-audit',
        hideWidgetTitle: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'admin-order-requests-widget',
        size: '1x1',
        displayName: 'platformWidget.admin.orderRequest.widgetName',
        component: OrderRequestsWidgetComponent,
        allowedRoles: [StaticRole.LEISTUNG_ADMIN],
        showInitial: true,
        routerLink: '/cartridge-admin/booking-requests',
        hideWidgetTitle: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'admin-contact-requests-widget',
        size: '1x1',
        displayName: 'platformWidget.admin.contactRequests.widgetName',
        component: ContactRequestsWidgetComponent,
        allowedRoles: [StaticRole.LEISTUNG_ADMIN],
        showInitial: true,
        routerLink: '/cartridge-admin/contact-requests',
        hideWidgetTitle: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'admin-price-requests-widget',
        size: '1x1',
        displayName: 'platformWidget.admin.priceRequests.widgetName',
        component: PriceRequestsWidgetComponent,
        allowedRoles: [StaticRole.LEISTUNG_ADMIN],
        showInitial: true,
        routerLink: '/cartridge-admin/price-requests/service-administrator',
        hideWidgetTitle: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'blocked-service-widget',
        size: '1x2',
        allowedRoles: [StaticRole.ORGANIZATION_ADMIN, StaticRole.ORGANIZATION_MEMBER],
        component: BlockedServiceWidgetComponent,
        isNotRemovable: true,
        showInitial: false,
        hideWidgetTitle: true,
      },
      {
        widgetType: 'PLATFORM',
        id: 'reassign-service-role-widget',
        size: '1x2',
        component: ReassignServiceRolesWidgetComponent,
        allowedRoles: [StaticRole.ORGANIZATION_ADMIN, StaticRole.ORGANIZATION_MANAGER],
        isNotRemovable: true,
        showInitial: false,
        hideWidgetTitle: true,
      },
    ],
    PLATFORM_CARTRIDGE: [
      {
        widgetType: 'PLATFORM_CARTRIDGE',
        id: 'usage-report',
        size: '1x2',
        displayName: 'platformCartridgeWidget.usageReport.displayName',
        component: UsageReportWidgetComponent,
        allowedRoles: [StaticRole.ORGANIZATION_ADMIN],
        showInitial: true,
        hideWidgetTitle: true,
      },
    ],
  };

  static readonly UNDRAGGABLE_WIDGET_IDS = [
    ...this.PLATFORM_WIDGETS.STATIC.map(id => id),
    'guided-tour-oa-admin-area',
    'admin-widget',
    'service-catalog-widget',
  ];
}
